/**
 * ApplePayButton - Airwallex Apple Pay Button Element
 * Renders Apple Pay button when available in the current environment
 */
import { useCheckoutTracking } from '@checkout/core'
import { forwardRef, useState, useCallback, useEffect, useRef } from 'react'

import styles from './ApplePayButton.module.css'
import { buildAirwallexCustomer } from './billing'
import { useAirwallex } from './Provider'
import { Skeleton } from '../../components'
import { withRetry } from '../../utils'

import type { AirwallexElement } from './Provider'
import type { CheckoutPayerInput, PaymentInteractionOutcome } from '@checkout/core'

export interface ApplePayButtonProps {
  /** Payment Intent ID */
  intentId: string
  /** Client Secret */
  clientSecret: string
  /** Payment amount (decimal, e.g., 99.00) */
  amount: string
  /** Currency code (e.g., 'USD', 'HKD') */
  currency: string
  /** Merchant country code (e.g., 'HK', 'US') */
  countryCode: string
  /** Origin URL for Apple Pay validation */
  origin?: string
  /** Business name displayed on Apple Pay sheet and bank statement */
  totalPriceLabel?: string
  /** Button type */
  buttonType?: 'buy' | 'pay' | 'plain' | 'order' | 'donate' | 'continue' | 'check-out' | 'subscribe'
  /** Button color */
  buttonColor?: 'black' | 'white' | 'white-outline'
  onOutcome: (outcome: PaymentInteractionOutcome) => void
  /** Called when Apple Pay availability is determined */
  onReady?: (available: boolean) => void
  /** Additional className */
  className?: string
  /** Whether this is a subscription payment */
  isSubscription?: boolean
  /** Customer ID for recurring payments (required when isSubscription is true) */
  customerId?: string
  /** Payer and billing context */
  payer?: CheckoutPayerInput
}

/** Element retry configuration */
const ELEMENT_RETRY_CONFIG = {
  maxRetries: 2,
  timeout: 8000,
  baseDelay: 500,
  maxDelay: 2000,
}

export const ApplePayButton = forwardRef<HTMLDivElement, ApplePayButtonProps>(
  (
    {
      intentId,
      clientSecret,
      amount,
      currency,
      countryCode,
      origin,
      totalPriceLabel = 'VigilTech Limited',
      buttonType = 'buy',
      buttonColor = 'black',
      onOutcome,
      onReady,
      className,
      isSubscription = false,
      customerId,
      payer,
    },
    ref
  ) => {
    const { createElement, isReady, error: sdkError } = useAirwallex()
    const { trackPayment } = useCheckoutTracking()
    const elementRef = useRef<AirwallexElement | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const mountedRef = useRef(false)

    // 使用 refs 存储回调，避免 effect 重复执行
    const onOutcomeRef = useRef(onOutcome)
    const onReadyRef = useRef(onReady)
    const trackPaymentRef = useRef(trackPayment)

    // 更新 refs
    useEffect(() => {
      onOutcomeRef.current = onOutcome
      onReadyRef.current = onReady
      trackPaymentRef.current = trackPayment
    })

    const [isElementReady, setIsElementReady] = useState(false)
    const [_isAvailable, setIsAvailable] = useState<boolean | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Cleanup element
    const cleanupElement = useCallback(() => {
      if (elementRef.current) {
        try {
          elementRef.current.unmount()
        } catch {
          // Ignore
        }
        elementRef.current = null
      }
      mountedRef.current = false
    }, [])

    // Initialize Apple Pay Element
    const initializeElement = useCallback(async () => {
      if (!createElement || !containerRef.current || !intentId || !clientSecret) {
        return
      }

      cleanupElement()
      setIsElementReady(false)
      setIsCreating(true)
      setError(null)

      try {
        const elementOptions: Record<string, unknown> = {
          intent_id: intentId,
          client_secret: clientSecret,
          amount: {
            value: amount,
            currency: currency.toUpperCase(),
          },
          countryCode: countryCode.toUpperCase(),
          origin: origin ?? window.location.origin,
          totalPriceLabel,
          buttonType,
          buttonColor,
          requiredBillingContactFields: ['name', 'email', 'postalAddress'],
        }

        // Subscription (recurring) payment
        if (isSubscription) {
          elementOptions.mode = 'recurring'
          elementOptions.customer_id = customerId
          elementOptions.totalPriceType = 'final'
          elementOptions.payment_consent = {
            next_triggered_by: 'merchant',
            merchant_trigger_reason: 'scheduled',
          }
        }

        const customer = buildAirwallexCustomer(payer)
        if (customer) {
          elementOptions.customer = customer
        }

        const applePayElement = await withRetry(async () => {
          return createElement('applePayButton', elementOptions)
        }, ELEMENT_RETRY_CONFIG)

        // Listen for ready event - this includes availability check
        applePayElement.on('ready', (data: unknown) => {
          const readyData = data as { canMakePayment?: boolean } | undefined
          const available = readyData?.canMakePayment ?? true
          setIsAvailable(available)
          setIsElementReady(true)
          trackPaymentRef.current(available ? 'applepay_available' : 'applepay_unavailable')
          onReadyRef.current?.(available)
        })

        // Listen for success event
        applePayElement.on('success', (event: unknown) => {
          // Handle both event.detail format and direct data format
          const eventObj = event as { detail?: unknown } | undefined
          const data = eventObj?.detail ?? event
          trackPaymentRef.current('applepay_submit_success')
          onOutcomeRef.current({
            status: 'succeeded',
            provider: 'airwallex',
            pmId: 'apple_pay',
            raw: data,
          })
        })

        // Listen for error event
        applePayElement.on('error', (event: unknown) => {
          // Handle both event.detail format and direct data format
          const eventObj = event as { detail?: unknown } | undefined
          const data = eventObj?.detail ?? event
          const errorData = data as { message?: string; error?: { message?: string } }
          const errorMessage =
            errorData?.message ?? errorData?.error?.message ?? 'Apple Pay payment failed'
          setError(errorMessage)
          trackPaymentRef.current('applepay_submit_error', { error_message: errorMessage })
          onOutcomeRef.current({
            status: 'failed',
            provider: 'airwallex',
            pmId: 'apple_pay',
            error: errorMessage,
            raw: data,
          })
        })

        // Mount to DOM
        applePayElement.mount('airwallex-applepay-container')
        elementRef.current = applePayElement
        mountedRef.current = true

        // Fallback: Mark as not available if no ready event after 3 seconds
        setTimeout(() => {
          if (!mountedRef.current) return
          setIsElementReady((prev) => {
            if (!prev) {
              setIsAvailable(false)
              onReadyRef.current?.(false)
              return true
            }
            return prev
          })
        }, 3000)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Apple Pay'
        setError(errorMessage)
        setIsAvailable(false)
        onReadyRef.current?.(false)
      } finally {
        setIsCreating(false)
      }
    }, [
      createElement,
      cleanupElement,
      intentId,
      clientSecret,
      amount,
      currency,
      countryCode,
      origin,
      totalPriceLabel,
      buttonType,
      buttonColor,
      isSubscription,
      customerId,
      payer,
    ])

    useEffect(() => {
      if (!isReady || !createElement || mountedRef.current) {
        return
      }

      void initializeElement()

      return cleanupElement
    }, [isReady, createElement, initializeElement, cleanupElement])

    // Show error if SDK failed
    if (sdkError || error) {
      return null
    }

    // 始终显示 Airwallex 按钮，让 Airwallex SDK 自己处理点击后的不可用提示
    const showSkeleton = !isElementReady || isCreating

    return (
      <div ref={ref} className={`${styles.container} ${className ?? ''}`}>
        {showSkeleton && <Skeleton height="60px" style={{ borderRadius: '4px' }} />}
        <div
          ref={containerRef}
          id="airwallex-applepay-container"
          className={styles.buttonWrapper}
          style={{ display: showSkeleton ? 'none' : undefined }}
        />
      </div>
    )
  }
)

ApplePayButton.displayName = 'ApplePayButton'
